/* eslint-disable camelcase */
import { useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import { useSelector } from "react-redux";

// Selectors
import * as userSelectors from "redux/user/selectors";

// Types
import { GameType } from "types/Games";
import { PaymentMethodType } from "types/PaymentMethodsTypes";
import { TournamentCardType } from "types/Tournament";

type GoogleEventRecord = Record<string, any>;

declare global {
  interface Window {
    dataLayer: GoogleEventRecord[];
  }
}

const GLORY_GTM = "GTM-KVM22BQC";
const BANGER_GTM = "GTM-PCZX9QB3";

export const useGTM = () => {
  const userId = useSelector(userSelectors.id) || "";
  const userData = useSelector(userSelectors.all) || "";
  const [updatedUserId, setUpdatedUserID] = useState(userId);
  // eslint-disable-next-line unused-imports/no-unused-vars
  const { balance, referrerBalance } = useSelector(userSelectors.all) || "";
  const email = useSelector(userSelectors.login) || "";
  const currency = useSelector(userSelectors.currency);
  const { VITE_APP_PROJECT_NAME } = import.meta.env;
  // eslint-disable-next-line unused-imports/no-unused-vars
  const currentTimestamp = Date.now();

  useEffect(() => {
    setUpdatedUserID(userId);
  }, [userId]);

  const addEvent = (data: GoogleEventRecord) => {
    const options = { ...data, timestamp: Math.floor(Date.now() / 1000) };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(options);
  };

  // eslint-disable-next-line sonarjs/no-identical-functions, unused-imports/no-unused-vars
  const addEventNoUserId = (data: GoogleEventRecord) => {
    const options = { ...data, timestamp: Math.floor(Date.now() / 1000) };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(options);
  };

  return {
    init: () => {
      if (
        import.meta.env.PROD &&
        !window.location.pathname.includes("dev") &&
        !window.location.pathname.includes("stage") &&
        !window.location.pathname.includes("localhost")
      ) {
        const tagManagerArgs = {
          gtmId: VITE_APP_PROJECT_NAME === "banger" ? BANGER_GTM : GLORY_GTM,
        };
        TagManager.initialize(tagManagerArgs);
      }
    },
    event: addEvent,
    openIntercom: () => {
      addEvent({
        event: "contact",
        method: "livechat",
        id: updatedUserId ?? userData.id,
      });
    },
    registrationStatus: (
      id: number,
      userEmail: string,
      status: string,
      error?: string | null,
      url?: string,
      clickId?: string,
      utmMedium?: string,
      utmSource?: string,
      utmCampaign?: string,
    ) => {
      addEvent({
        event: "registration",
        event_category: "registration",
        eventAction: "click",
        eventStatus: status,
        firingOptions: "oncePerEvent",
        error: error ?? null,
        email: userEmail ?? email,
        id: id ?? updatedUserId,
        currency,
        // eslint-disable-next-line camelcase
        page_location: url || "",
        "utm-source": utmSource,
        "utm-medium": utmMedium,
        "utm-campaign": utmCampaign,
        clickId,
        referrer: document.referrer,
        userId,
      });
    },
    onCheckUser: () => {
      addEvent({
        id: updatedUserId,
      });
    },
    onOpenLoginModal: () => {
      addEvent({
        event: "login_start",
        event_category: "login",
        id: updatedUserId,
      });
    },
    onLoginSuccess: () => {
      addEvent({
        event: "login",
        event_category: "login",
        id: updatedUserId,
        userId,
      });
    },
    onLoginError: (errorField: string) => {
      addEvent({
        event: "login_error",
        event_category: "login",
        id: updatedUserId,
        label: errorField,
      });
    },
    onRegistrationStart: () => {
      addEvent({
        event: "registration_start",
        event_category: "registration",
        id: updatedUserId,
        userId,
        userData,
      });
    },
    onRegistrationError: (errorField: string, error: string) => {
      addEvent({
        event: "registration_error",
        event_category: "registration",
        id: updatedUserId,
        label: errorField,
        errorField,
        error,
      });
    },
    onBannerView: (label: string, bannerView: any) => {
      addEvent({
        event: "banner_view",
        event_category: "banner",
        id: updatedUserId,
        label,
        event_context: bannerView,
      });
    },
    onBannerClick: (label: string, id: number, bannerView?: number) => {
      addEvent({
        event: "banner_click",
        event_category: "banner",
        id: updatedUserId,
        label: label.toString(),
        event_context: id,
        banner_view: bannerView,
      });
    },
    onPopupCollectPhoneOpen: () => {
      addEvent({
        event: "popup_appear",
        event_category: "popup",
        user_id: updatedUserId,
        event_context: "first_popup_render",
      });
    },
    onPopupCollectPhoneClose: () => {
      addEvent({
        event: "popup_close",
        event_category: "popup",
        user_id: updatedUserId,
        event_context: "popup_close",
        action: "close",
      });
    },
    onPopupCollectPhoneSuccess: (phoneNumber: string) => {
      addEvent({
        event: "popup_interact",
        event_category: "popup",
        user_id: updatedUserId,
        event_context: phoneNumber,
        action: "send_phone",
      });
    },
    onPopupCollectPhoneError: (errorMessage: string) => {
      addEvent({
        event: "popup_error",
        event_category: "popup",
        user_id: updatedUserId,
        event_context: "popup_error",
        action: errorMessage,
      });
    },
    openRegistration: () => {
      // addEvent({
      //   event: "page-view",
      //   category: "registration",
      //   id: updatedUserId,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    changeLanguage: (language: string) => {
      // addEvent({
      //   event: "change_language",
      //   category: "language_switch",
      //   action: "click",
      //   label: language,
      //   firingOptions: "oncePerEvent",
      // });
    },
    openLogin: () => {
      // addEvent({
      //   event: "page-view",
      //   category: "login",
      //   id: updatedUserId,
      // });
    },
    clickMobileMenu: () => {
      // addEvent({
      //   event: "open-burger-menu",
      //   category: "step-1",
      //   id: updatedUserId,
      // });
    },
    clickProfilePage: () => {
      // addEvent({
      //   event: "page-view",
      //   category: "account",
      //   id: updatedUserId,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickSaveProfile: (data) => {
      // addEvent({
      //   event: "update-userinfo",
      //   email,
      //   name: data.name,
      //   "birth-date": data.birthday,
      //   country: data.country,
      //   id: updatedUserId,
      //   currency: data.currency,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    inputAccountChange: (value) => {
      // addEvent({
      //   event: "button_click",
      //   category: "change_name",
      //   email,
      //   name: "",
      //   "new name": value,
      //   id: userData.id,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickChangeLanguage: (language) => {
      // addEvent({
      //   event: "change-language",
      //   category: "change",
      //   language,
      //   id: updatedUserId,
      // });
    },
    // eslint-disable-next-line @typescript-eslint/no-shadow, unused-imports/no-unused-vars
    clickSupportEmail: (email) => {
      // addEvent({
      //   event: "contact",
      //   method: "email",
      //   email,
      //   id: updatedUserId,
      // });
    },
    openSupportPage: () => {
      // addEvent({
      //   event: "contact",
      //   method: "email",
      //   id: updatedUserId,
      // });
    },
    clickGetWelcomeBonus: () => {
      // addEvent({
      //   event: "page-view",
      //   category: "get-bonus-0",
      //   id: updatedUserId,
      // });
    },
    clickGetWelcomeBonusAccept: () => {
      // addEvent({
      //   event: "page-view",
      //   category: "get-bonus-1",
      //   id: updatedUserId,
      // });
    },
    clickTransactionHistory: () => {
      // addEvent({
      //   event: "page-view",
      //   category: "transaction-history",
      //   balance,
      //   id: updatedUserId,
      // });
    },
    clickGameHistory: () => {
      // addEvent({
      //   event: "page-view",
      //   category: "game-history",
      //   id: updatedUserId ?? userData.id,
      // });
    },
    clickBonusPage: () => {
      // addEvent({
      //   event: "page-view",
      //   category: "bonus-page",
      //   id: updatedUserId ?? userData.id,
      // });
    },
    clickBonusReject: () => {
      // addEvent({
      //   event: "bonus-reject",
      //   // eslint-disable-next-line sonarjs/no-duplicate-string
      //   category: "bonus-program",
      //   id: updatedUserId,
      // });
    },
    clickBonusAccept: () => {
      // addEvent({
      //   event: "bonus-accept",
      //   category: "bonus-program",
      // });
    },
    clickAviator: () => {
      // addEvent({
      //   // eslint-disable-next-line sonarjs/no-duplicate-string
      //   event: "choice-category",
      //   category: "aviator",
      //   id: updatedUserId ?? userData.id,
      // });
    },
    clickVSport: () => {
      // addEvent({
      //   event: "choice-category",
      //   category: "betting",
      //   id: updatedUserId ?? userData.id,
      // });
    },
    clickTournaments: () => {
      // addEvent({
      //   event: "page-view",
      //   category: "tournaments",
      //   id: updatedUserId ?? userData.id,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickStartTournament: (tournament: TournamentCardType) => {
      // addEvent({
      //   event: "start-tournament",
      //   category: "tournaments",
      //   tournamentName: tournament.name,
      //   tournamentId: tournament.id,
      //   email,
      //   id: updatedUserId,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickTournamentGamePlay: (game: GameType, tournamentId, tournamentName) => {
      // eslint-disable-next-line no-console
      // console.log(
      //   "start-game-tournament",
      //   game.name,
      //   game.provider.name,
      //   tournamentName,
      //   tournamentId,
      //   updatedUserId,
      //   email,
      // );
      // addEvent({
      //   event: "start-game-tournament",
      //   // eslint-disable-next-line camelcase
      //   name_of_game: game.name,
      //   // eslint-disable-next-line camelcase
      //   name_of_provider: game.provider.name, // name of provider
      //   tournamentName,
      //   tournamentId,
      //   id: updatedUserId ?? userData.id,
      //   email,
      // });
    },
    clickTournamentGamePlayDemo: (
      // eslint-disable-next-line unused-imports/no-unused-vars
      game: GameType,
      // eslint-disable-next-line unused-imports/no-unused-vars
      tournamentId,
      // eslint-disable-next-line unused-imports/no-unused-vars
      tournamentName,
    ) => {
      // eslint-disable-next-line no-console
      // console.log(
      //   "start-demo-tournament",
      //   game.name,
      //   game.provider.name,
      //   tournamentName,
      //   tournamentId,
      //   updatedUserId,
      //   email,
      // );
      // addEvent({
      //   event: "start-demo-tournament",
      //   // eslint-disable-next-line camelcase
      //   "name of provider": game.provider.name, // name of provider
      //   // eslint-disable-next-line camelcase
      //   "name of game": game.name,
      //   tournamentName,
      //   tournamentId,
      //   id: updatedUserId,
      // });
    },
    clickBonusProgramm: () => {
      // addEvent({
      //   event: "page-view",
      //   category: "bonus-program",
      //   id: updatedUserId,
      // });
    },
    clickTelegram: () => {
      // addEvent({
      //   event: "goto",
      //   category: "telegram",
      //   id: updatedUserId,
      // });
    },
    clickInstagram: () => {
      // addEvent({
      //   event: "goto",
      //   category: "instagram",
      //   id: updatedUserId,
      // });
    },
    clickFacebook: () => {
      // addEvent({
      //   event: "goto",
      //   category: "Facebook",
      //   id: updatedUserId,
      // });
    },
    clickWhatsapp: () => {
      // addEvent({
      //   event: "goto",
      //   category: "Whatsapp",
      //   id: updatedUserId,
      // });
    },
    clickLogout: () => {
      // addEvent({
      //   event: "logout",
      //   email,
      //   id: updatedUserId ?? userData.id,
      // });
    },
    clickLiveCasino: () => {
      // addEvent({
      //   event: "page-view",
      //   category: "main-page",
      //   id: updatedUserId ?? userData.id,
      // });
    },
    clickTvGames: () => {
      // addEvent({
      //   event: "choice-category",
      //   category: "tv-games",
      //   id: updatedUserId ?? userData.id,
      // });
    },
    clickLiveCasinoDesktop: () => {
      // addEvent({
      //   event: "choice-category",
      //   category: "live-casino",
      //   id: updatedUserId ?? userData.id,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    openMainPage: (currentUserId: number) => {
      // addEvent({
      //   event: "page-view",
      //   category: "main-page",
      //   id: currentUserId || "",
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickStartRealGame: (game: GameType) => {
      // eslint-disable-next-line no-console
      console.log("clickStartRealGame");
      // addEvent({
      //   event: "start-game",
      //   "name of provider": game.provider.name,
      //   "name of game": game.name,
      //   id: updatedUserId ?? userData.id,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickStartDemoGame: (game: GameType) => {
      // eslint-disable-next-line no-console
      console.log("clickStartDemoGame");
      // addEvent({
      //   event: "start-demo",
      //   "name of provider": game.provider.name,
      //   "name of game": game.name,
      //   id: updatedUserId ?? userData.id,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickAddFavorite: (game: GameType | null) => {
      // addEvent({
      //   event: "add-favorites",
      //   "name of provider": game ? game.provider.name : "",
      //   "name of game": game ? game.name : "",
      //   id: updatedUserId,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickRemoveFavorite: (game: GameType | null) => {
      // addEvent({
      //   event: "delete-favorites",
      //   "name of provider": game ? game.provider.name : "",
      //   "name of game": game ? game.name : "",
      //   id: updatedUserId,
      // });
    },
    openProvidersFilter: () => {
      // addEvent({
      //   event: "open-list-providers",
      //   id: updatedUserId,
      // });
    },
    closeProvidersFilter: () => {
      // addEvent({
      //   event: "close-list-providers",
      //   id: updatedUserId,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickProvidersFilterChoose: (name: string) => {
      // addEvent({
      //   event: "choice-provider",
      //   "name of provider": name,
      //   id: updatedUserId,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    deleteProvidersFilterChoose: (name: string) => {
      // addEvent({
      //   event: "delete-provider",
      //   "name of provider": name,
      //   id: updatedUserId,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickLoginError: (userEmail: string, id: number | null) => {
      // addEvent({
      //   event: "login",
      //   category: "error",
      //   email: userEmail ?? email,
      //   id: id ?? updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-shadow
    clickLogin: (email, id) => {
      // addEventNoUserId({
      //   event: "login",
      //   email,
      //   id,
      //   userId: updatedUserId,
      // });
    },
    openDeposit: () => {
      // addEvent({
      //   event: "deposit",
      //   category: "dep-step-0",
      //   id: updatedUserId,
      //   userId: updatedUserId,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    openDepositProfile: (path) => {
      // addEvent({
      //   event: "deposit",
      //   category: "dep-step-0_profile",
      //   referer: path,
      //   email,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    closeDepositSuccess: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "play",
      //   balance,
      //   email,
      //   id: updatedUserId ?? userData.id,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line @typescript-eslint/no-shadow, unused-imports/no-unused-vars
    closeDepositError: (method: any, currency: any, amount: any) => {
      // addEvent({
      //   event: "deposit",
      //   category: "declined_transaction",
      //   "payment-method ": method,
      //   currency,
      //   amount,
      //   balance,
      //   "payment-status": "error",
      //   email,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },

    // eslint-disable-next-line unused-imports/no-unused-vars
    depositSubmit: (method: PaymentMethodType, amount: string) => {
      // addEvent({
      //   event: "form_submit",
      //   category: "deposit",
      //   "payment-method": method ?? "",
      //   sum: amount ?? "",
      //   balance: balance ?? "",
      //   phone: userData?.login ?? "",
      //   email: email ?? "",
      //   id: userId ?? updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    withdrawlSubmit: (method: PaymentMethodType | null, amount: string) => {
      // addEvent({
      //   event: "withdrawl",
      //   category: "success",
      //   "payment-method": method ?? "",
      //   sum: amount ?? "",
      //   balance: balance ?? "",
      //   phone: userData?.login ?? "",
      //   email: email ?? "",
      //   id: userId ?? "",
      //   timestamp: currentTimestamp,
      // });
    },
    withdrawlSubmitStep1: () => {
      // addEvent({
      //   event: "withdrawl",
      //   category: "withdrawl-step1",
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      //   gtmUniqueEventId: 160,
      //   balance,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    withdrawlSubmitStep2: (method: PaymentMethodType | null) => {
      // addEvent({
      //   event: "withdrawl",
      //   category: "withdrawl-step2",
      //   id: updatedUserId,
      //   paymentMethod: method,
      //   timestamp: currentTimestamp,
      //   gtmUniqueEventId: 163,
      //   balance,
      // });
    },
    withdrawlSubmitStep3: (
      // eslint-disable-next-line unused-imports/no-unused-vars
      method: PaymentMethodType | null,
      // eslint-disable-next-line unused-imports/no-unused-vars
      amount: number,
      // eslint-disable-next-line
      // eslint-disable-next-line unused-imports/no-unused-vars @typescript-eslint/no-shadow, @typescript-eslint/no-shadow, unused-imports/no-unused-vars
      currency: string,
    ) => {
      // addEvent({
      //   event: "withdrawl",
      //   category: "withdrawl-step3",
      //   id: updatedUserId,
      //   paymentMethod: "method",
      //   amount,
      //   currency,
      //   phoneNumber: userData?.login,
      //   timestamp: currentTimestamp,
      //   gtmUniqueEventId: 168,
      //   balance,
      // });
    },
    withdrawlSubmitError: (
      // eslint-disable-next-line unused-imports/no-unused-vars
      method: PaymentMethodType | null,
      // eslint-disable-next-line unused-imports/no-unused-vars
      amount: string,
    ) => {
      // addEvent({
      //   event: "withdrawl",
      //   category: "error",
      //   "payment-method": method ?? "",
      //   sum: amount ?? "",
      //   balance: balance ?? "",
      //   phone: userData?.login ?? "",
      //   email: email ?? "",
      //   id: userId ?? "",
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    chooseDepositMethod: (method: PaymentMethodType) => {
      // addEvent({
      //   event: "deposit",
      //   "payment-method": method.name,
      //   category: "dep-step-1",
      //   id: updatedUserId,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    openAmountStep: (method: PaymentMethodType, amount: string) => {
      // addEvent({
      //   event: "deposit",
      //   "payment-method": method.name,
      //   amount: amount || "",
      //   currency: currency?.isoCode || "",
      //   category: "dep-step-2",
      //   id: updatedUserId,
      // });
    },
    openTransactionStep: (
      // eslint-disable-next-line unused-imports/no-unused-vars
      method: PaymentMethodType,
      // eslint-disable-next-line unused-imports/no-unused-vars
      account: string,
      // eslint-disable-next-line unused-imports/no-unused-vars
      amount: string,
      // eslint-disable-next-line unused-imports/no-unused-vars
      transaction: string,
    ) => {
      // addEvent({
      //   event: "deposit",
      //   "payment-method": method.name,
      //   "payment-account": account,
      //   "payment-system": method.system,
      //   amount: amount || "",
      //   currency: currency?.isoCode,
      //   "transaction-number": transaction || "",
      //   category: "dep-step-3",
      //   id: updatedUserId,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    openWithdraw: () => {
      // addEvent({
      //   event: "page-view",
      //   category: "withdrawl",
      //   id: updatedUserId,
      //   balance,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    chooseWithdrawMethod: (method: PaymentMethodType) => {
      // addEvent({
      //   event: "withdrawl",
      //   "payment-method": method.name,
      //   category: "choice-method",
      //   id: updatedUserId,
      //   balance,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickWithdraw: (
      // eslint-disable-next-line unused-imports/no-unused-vars
      method: PaymentMethodType,
      // eslint-disable-next-line unused-imports/no-unused-vars
      amount: string,
      // eslint-disable-next-line unused-imports/no-unused-vars
      phone: string,
    ) => {
      // addEvent({
      //   event: "withdrawl",
      //   "payment-method": method.name, // payment-method
      //   amount: amount || "", // amount
      //   currency: currency?.isoCode || "", // currency
      //   "phone-number": phone || "", // phone-number
      //   category: "payout",
      //   id: updatedUserId,
      //   balance,
      // });
    },
    withdrawlSubmitAnyway: (
      // eslint-disable-next-line unused-imports/no-unused-vars
      method: PaymentMethodType | null,
      // eslint-disable-next-line unused-imports/no-unused-vars
      amount: string,
    ) => {
      // addEvent({
      //   event: "withdrawl",
      //   category: "withdrawl-anyway",
      //   "payment-method": method ?? "",
      //   sum: amount ?? "",
      //   balance: balance ?? "",
      //   phone: userData?.login ?? "",
      //   email: email ?? "",
      //   id: userId ?? "",
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickCategoryChoise: (name: string) => {
      // addEvent({
      //   event: "choice-category",
      //   eventCategory: "choice",
      //   eventAction: "click",
      //   eventLabel: name,
      //   category: name,
      //   id: updatedUserId ?? userData.id,
      //   firingOptions: "oncePerEvent",
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    gameSearchKeyword: (keyword: string) => {
      // addEvent({
      //   event: "search",
      //   keyword, // результат поиска
      //   id: updatedUserId,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    providerSearchKeyword: (keyword: string) => {
      // addEvent({
      //   event: "search-provider",
      //   keyword, // результат поиска
      //   id: updatedUserId,
      // });
    },
    clickFavoriteTB: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "favorites_tb",
      //   id: updatedUserId,
      // });
    },
    clickDepTB: () => {
      // addEvent({
      //   event: "deposit",
      //   category: "dep-step-0_tb",
      //   id: updatedUserId,
      // });
    },

    clickCasinoTB: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "casino_tb",
      //   id: updatedUserId,
      // });
    },
    clickTournamentTB: () => {
      // addEvent({
      //   event: "choice-category",
      //   category: "tournament_tb",
      //   id: updatedUserId ?? userData.id,
      // });
    },
    clickMenuTB: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "menu_tb",
      //   id: updatedUserId,
      // });
    },
    infoAboutUs: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "about us",
      //   email,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    infoTerms: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "terms",
      //   email,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    infoKYC: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "kyc",
      //   email,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    infoPrivacy: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "privacy",
      //   email,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    infoResponsable: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "responsable",
      //   email,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    infoCookies: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "cookies",
      //   email,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickBanner: (elementUrl: any, eventName: any, position: number) => {
      // addEvent({
      //   event: "button_click",
      //   category: "head_banner",
      //   elementUrl,
      //   // eslint-disable-next-line camelcase
      //   event_name: eventName,
      //   id: updatedUserId,
      //   position,
      //   timestamp: currentTimestamp,
      // });
    },
    clickPasswordRecovery: () => {
      // addEvent({
      //   event: "try-restore-access",
      // });
    },
    clickAuth: () => {
      // addEvent({
      //   event: "login",
      //   email,
      //   id: updatedUserId,
      // });
    },
    clickPlayUpdated: () => {
      // addEvent({
      //   event: "play-in-update",
      //   id: updatedUserId,
      //   email,
      // });
    },
    clickUpdatePage: () => {
      // addEvent({
      //   event: "about-update",
      //   id: updatedUserId,
      //   email,
      // });
    },
    clickSupport: () => {
      // addEvent({
      //   event: "support",
      //   id: updatedUserId,
      //   email,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    depositResult: (eventStatus: string) => {
      // addEvent({
      //   event: "deposit",
      //   eventCategory: "dep-success-transaction-5",
      //   eventAction: "click",
      //   eventStatus,
      //   // eslint-disable-next-line camelcase
      //   email,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      //   referrer: document.referrer,
      //   firingOptions: "oncePerEvent",
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickSpinWheel: (name: string) => {
      // addEvent({
      //   event: "spin",
      //   eventCategory: "interactions",
      //   eventAction: "button_click",
      //   // eslint-disable-next-line camelcase
      //   button_click: name,
      //   timestamp: currentTimestamp,
      //   firingOptions: "oncePerEvent",
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickSpinAgainWheel: (name: string) => {
      // addEvent({
      //   event: "spin_again",
      //   eventCategory: "interactions",
      //   eventAction: "button_click",
      //   // eslint-disable-next-line camelcase
      //   button_click: name,
      //   timestamp: currentTimestamp,
      //   firingOptions: "oncePerEvent",
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    wheelRegistrationResult: (result: string) => {
      // addEvent({
      //   event: "registration",
      //   eventCategory: "interactions",
      //   eventAction: "click",
      //   eventStatus: result,
      //   firingOptions: "oncePerEvent",
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    restorePasswordResult: (result: string) => {
      // addEvent({
      //   event: "registration",
      //   eventCategory: "interactions",
      //   eventAction: "button_click",
      //   eventStatus: result,
      //   firingOptions: "oncePerEvent",
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickWheelCurrencyСhoice: (choseCurrency: string | number) => {
      // addEvent({
      //   event: "currency_choice",
      //   eventCategory: "interactions",
      //   eventAction: "button_click",
      //   currency: choseCurrency,
      //   firingOptions: "oncePerEvent",
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickLandGetBonus: (name: string | number) => {
      // addEvent({
      //   event: "get_bonus",
      //   eventCategory: "interactions",
      //   eventAction: "button_click",
      //   // eslint-disable-next-line camelcase
      //   Name_of_provider: name,
      //   firingOptions: "oncePerEvent",
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationWelcome: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "play_ftd_popup",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickCloseNotificationWelcome: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "play_ftd_popup_close",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationUtilizationDeposit: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "deposit_popup",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationUtilizationDepositWithChat: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "deposit_popup_chat",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickCloseNotificationUtilizationDeposit: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "deposit_popup_close",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickCloseNotificationUtilizationDepositWithChat: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "deposit_popup_chat_close",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationUtilizationDepositSupport: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "deposit_popup_support",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    clickInviteFriend: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "invite_friend",
      //   email,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    clickCopyLinkInviteFriend: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "copy_link_invite",
      //   email,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    clickGetBonusInviteFriend: () => {
      // addEvent({
      //   event: "button_click",
      //   category: "get_invite_bonus",
      //   email,
      //   balance: Number(balance) / 100,
      //   // eslint-disable-next-line camelcase
      //   balance_ref: Number(referrerBalance) / 100,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickSendInviteFriend: (amount: number | string, referer: string) => {
      // addEvent({
      //   event: "popup_bonus_invite",
      //   category: amount ? "success" : "error",
      //   email,
      //   amount,
      //   balance: Number(balance) / 100,
      //   // eslint-disable-next-line camelcase
      //   balance_ref: Number(referrerBalance / 100),
      //   referer,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickSuccessInviteFriend: (amount: string, referer: string) => {
      // addEvent({
      //   event: "popup_bonus_invite",
      //   category: "page_success",
      //   email,
      //   amount,
      //   balance: Number(balance) / 100,
      //   // eslint-disable-next-line camelcase
      //   balance_ref: Number(referrerBalance / 100),
      //   referer,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    clickRulesInviteFriend: (referer: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "rules",
      //   referer,
      //   email,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    onRegistrationReferral: (
      rParam: string,
      referer: string,
      id,
      userEmail,
      currencyIso,
    ) => {
      // eslint-disable-next-line no-console
      console.log("onRegistrationReferral_new", id, userEmail, currencyIso);
      // addEvent({
      //   event: "registration_referral",
      //   "referral-ID": rParam,
      //   email: userEmail,
      //   currency: currencyIso,
      //   referer,
      //   id,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    submitEmptyWithdrawalFriend: (amount: number | string, referer: string) => {
      // addEvent({
      //   event: "popup_bonus_invite",
      //   category: "error",
      //   email,
      //   amount,
      //   balance: Number(balance) / 100,
      //   // eslint-disable-next-line camelcase
      //   balance_ref: Number(referrerBalance / 100),
      //   referer,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationFirstDeposit: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "play_deposit_popup",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationFirstDepositWithChat: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "play_deposit_popup_chat",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickCloseNotificationFirstDeposit: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "play_deposit_popup_close",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickCloseNotificationFirstDepositWithChat: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "play_deposit_popup_chat_close",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationFirstDepositSupport: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "play_deposit_popup_support",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationErrorDeposit: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "error1_deposit_popup",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickCloseNotificationErrorDeposit: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "error1_deposit_popup_close",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationSecondErrorDepositWithChat: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "error2_deposit_popup_chat",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickCloseNotificationSecondErrorDepositWithChat: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "error2_deposit_popup_chat_close",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationSecondErrorDepositWithChatSupport: (path: string) => {
      // addEvent({
      //   event: "contact",
      //   category: "error2_support",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationSecondErrorDepositWithoutChat: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "error2_deposit_popup",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickCloseNotificationSecondErrorDepositWithoutChat: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "error2_deposit_popup_close",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationThirdErrorDepositWithChat: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "error3_deposit_popup_chat",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickCloseNotificationThirdErrorDepositWithChat: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "error3_deposit_popup_chat_close",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationThirdErrorDepositWithChatSupport: (path: string) => {
      // addEvent({
      //   event: "contact",
      //   category: "error3_support",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickNotificationThirdErrorDepositWithoutChat: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "error3_deposit_popup",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    playClickCloseNotificationThirdErrorDepositWithoutChat: (path: string) => {
      // addEvent({
      //   event: "button_click",
      //   category: "error3_deposit_popup_close",
      //   email: email ?? "",
      //   referer: path,
      //   id: updatedUserId,
      //   timestamp: currentTimestamp,
      // });
    },
    // eslint-disable-next-line unused-imports/no-unused-vars
    viewBanner(eventName: string, position: number) {
      // addEvent({
      //   event: "banner-view",
      //   // eslint-disable-next-line camelcase
      //   name_of_banner: eventName,
      //   id: userId,
      //   email,
      //   position,
      // });
    },
  };
};
